import { CSSProperties, ReactNode, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  COMPONENT_LABELS_EVENT,
  areComponentLabelsEnabled,
  setComponentLabelOverride
} from '../../lib/config/componentLabelsFlag';
import { useAppSelector } from '../../store/store';

type ComponentLabelPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

type ComponentLabelProps = {
  name: string;
  position?: ComponentLabelPosition;
  offset?: number;
  className?: string;
  style?: CSSProperties;
  portalTarget?: HTMLElement;
};

const DEFAULT_POSITION: ComponentLabelPosition = 'bottom-right';
const DEFAULT_OFFSET = 20;

const buildPositionStyle = (position: ComponentLabelPosition, offset: number): CSSProperties => {
  switch (position) {
    case 'top-left':
      return { top: offset, left: offset };
    case 'top-right':
      return { top: offset, right: offset };
    case 'bottom-left':
      return { bottom: offset, left: offset };
    case 'bottom-right':
    default:
      return { bottom: offset, right: offset };
  }
};

const baseStyle: CSSProperties = {
  position: 'fixed',
  pointerEvents: 'auto',
  zIndex: 2147483000,
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'none',
  color: '#fde68a',
  backgroundColor: 'rgba(24, 24, 27, 0.85)',
  border: '1px solid rgba(250, 204, 21, 0.6)',
  padding: '4px 10px',
  borderRadius: 999,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
  backdropFilter: 'blur(6px)',
  maxWidth: 'calc(100vw - 40px)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  userSelect: 'text',
  cursor: 'text'
};

export const useComponentLabelsEnabled = (): boolean => {
  const userId = useAppSelector((state) => state.auth.user?.id?.trim() ?? null);
  const [enabled, setEnabled] = useState<boolean>(() => areComponentLabelsEnabled(userId));

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleRefresh = () => {
      setEnabled(areComponentLabelsEnabled(userId));
    };

    window.addEventListener('storage', handleRefresh);
    window.addEventListener(COMPONENT_LABELS_EVENT, handleRefresh);

    return () => {
      window.removeEventListener('storage', handleRefresh);
      window.removeEventListener(COMPONENT_LABELS_EVENT, handleRefresh);
    };
  }, [userId]);

  // Update enabled state when userId changes
  useEffect(() => {
    setEnabled(areComponentLabelsEnabled(userId));
  }, [userId]);

  return enabled;
};

const usePortalTarget = (active: boolean, portalTarget?: HTMLElement | null): HTMLElement | null => {
  const [target, setTarget] = useState<HTMLElement | null>(() => portalTarget ?? null);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (!active) {
      setTarget(portalTarget ?? null);
      return;
    }

    if (portalTarget) {
      setTarget(portalTarget);
      return;
    }

    const id = 'cmf-component-label-layer';
    let node = document.getElementById(id);
    if (!node) {
      node = document.createElement('div');
      node.setAttribute('id', id);
      node.style.pointerEvents = 'none';
      node.style.position = 'fixed';
      node.style.inset = '0px';
      node.style.zIndex = '2147483000';
      document.body.appendChild(node);
    }
    setTarget(node);
  }, [active, portalTarget]);

  return target;
};

export const ComponentLabel = ({
  name,
  position = DEFAULT_POSITION,
  offset = DEFAULT_OFFSET,
  className,
  style,
  portalTarget
}: ComponentLabelProps) => {
  const enabled = useComponentLabelsEnabled();
  const target = usePortalTarget(enabled, portalTarget);

  const inlineStyle = useMemo(() => {
    const computedPosition = buildPositionStyle(position, offset);
    return {
      ...baseStyle,
      ...computedPosition,
      ...style
    } satisfies CSSProperties;
  }, [position, offset, style]);

  if (!enabled || !target) {
    return null;
  }

  const content = (
    <div
      data-component-label={name}
      aria-hidden='true'
      className={className ?? 'ComponentLabel'}
      style={inlineStyle}
    >
      {name}
    </div>
  );

  return createPortal(content, target);
};

type UseComponentLabelOptions = Omit<ComponentLabelProps, 'name'>;

export const useComponentLabel = (name: string, options?: UseComponentLabelOptions): ReactNode => {
  const { position, offset, className, style, portalTarget } = options ?? {};

  return useMemo(() => {
    return (
      <ComponentLabel
        key={`component-label:${name}`}
        name={name}
        position={position}
        offset={offset}
        className={className}
        style={style}
        portalTarget={portalTarget}
      />
    );
  }, [name, position, offset, className, style, portalTarget]);
};

export const toggleComponentLabels = (enabled: boolean | null): void => {
  setComponentLabelOverride(enabled);
};
